/**
 * O terreno agindo sobre **um tanque qualquer**.
 *
 * O jogador tinha tudo: perdia velocidade na água, derrapava no gelo, pegava
 * fogo na lava e continuava queimando depois de sair, deixava rastro de lama e
 * risco de gelo no chão. O inimigo tinha um multiplicador de velocidade e uma
 * regra que tratava a lava como parede — ele parava na beira da poça como se
 * fosse um muro invisível. Duas físicas diferentes no mesmo chão, e o jogador
 * enxergava a diferença na hora: dava para prender um bot na borda da lava.
 *
 * Este módulo existe para que só exista uma. Ele guarda:
 *
 *  - **o registro de estado por tanque** — quem está em chamas, encharcado ou
 *    enlameado, indexado por id (o jogador, cada bot, cada jogador remoto);
 *  - **a matemática de movimento sobre o piso** — derrapagem e inércia, as
 *    mesmas funções que o tanque do jogador usa desde sempre;
 *  - **a decisão de rastro** — qual marca o tanque deixa no chão agora.
 *
 * Quem aplica dano é sempre o dono da entidade: o jogo local decide pelo
 * jogador e pelos bots; no multijogador o servidor decide por todos. O estado
 * daqui é derivado da posição, então qualquer peer chega no mesmo resultado
 * olhando um tanque andar — e é isso que permite ver o bot do amigo pegando
 * fogo sem gastar um byte de rede com isso.
 */

import {
  frameRateAdjusted,
  glideDecay,
  tickSurfaceStatus,
  NEUTRAL_STATUS,
  type StatusModifiers,
  type StatusTick,
  type SurfaceStatus,
  type SurfaceType,
} from "@shared/arenaTerrain";

import { arenaSurface, sampleSurface } from "./arenaSurface";

/** Id do tanque do jogador local no registro. */
export const PLAYER_SURFACE_ID = "player";

const NO_MODS: StatusModifiers = { hazardResist: 0 };

/**
 * Estado por tanque.
 *
 * `Map` e não store: é escrito a cada frame por cada tanque em cena. O que a UI
 * precisa saber (o jogador está pegando fogo?) é espelhado no store só quando
 * muda de fato — duas ou três vezes por travessia, não sessenta por segundo.
 */
const registry = new Map<string, SurfaceStatus>();

export function getTankSurfaceStatus(id: string): Readonly<SurfaceStatus> {
  return registry.get(id) ?? NEUTRAL_STATUS;
}

export function isTankBurning(id: string, now: number): boolean {
  const status = registry.get(id);
  return status !== undefined && now < status.burningUntil;
}

/**
 * Avança o estado de um tanque a partir de onde ele está.
 *
 * Devolve o tick completo (dano por segundo e viradas de estado) para quem
 * chamou decidir o que fazer: o jogo local cobra a vida, o cliente
 * multijogador só acende as chamas e deixa o dano com o servidor.
 */
export function tickTankSurface(
  id: string,
  x: number,
  z: number,
  now: number,
  mods: StatusModifiers = NO_MODS,
): StatusTick & { surface: SurfaceType } {
  const surface: SurfaceType = arenaSurface.active ? sampleSurface(x, z) : "ground";
  const current = registry.get(id) ?? NEUTRAL_STATUS;
  const tick = tickSurfaceStatus(current, surface, now, mods);

  // Sem arena com terreno o registro não deve crescer: numa partida de 16
  // jogadores em arena lisa isso seria um mapa de 16 objetos inúteis mantidos
  // vivos por toda a partida.
  if (
    tick.status.burningUntil > now ||
    tick.status.soakedUntil > now ||
    tick.status.muddyUntil > now
  ) {
    const stored = registry.get(id);
    if (stored) {
      stored.burningUntil = tick.status.burningUntil;
      stored.soakedUntil = tick.status.soakedUntil;
      stored.muddyUntil = tick.status.muddyUntil;
    } else {
      registry.set(id, { ...tick.status });
    }
  } else if (registry.has(id)) {
    registry.delete(id);
  }

  return { ...tick, surface };
}

/** Tanque saiu de cena (morreu, desconectou, o componente desmontou). */
export function releaseTankSurface(id: string): void {
  registry.delete(id);
}

/** Nova arena, novo modo, volta ao menu: ninguém carrega fogo do jogo anterior. */
export function resetTankSurfaces(): void {
  registry.clear();
}

// ============================================
// A LAVA VISTA POR UMA IA
// ============================================

/**
 * Tem lava em (x, z)?
 *
 * A lava é o único piso que a IA precisa consultar por vontade própria: os
 * outros ela sofre andando por cima, este ela tenta não pisar. Vive aqui, e não
 * dentro de um controlador, porque os dois caminhos de bot do jogo (o tanque
 * completo e o enxame instanciado) precisam da mesma resposta — quando eram
 * duas funções, só uma delas existia.
 */
export function isLavaAt(x: number, z: number): boolean {
  return arenaSurface.active && sampleSurface(x, z) === "lava";
}

/**
 * Para onde correr quando já se está dentro da poça.
 *
 * Varre anéis de direções e devolve o primeiro ponto que sai do perigo. É a
 * mesma decisão que uma criança toma no controle: saiu da lava, pensa depois.
 * Sem isto, um bot empurrado para dentro (coice de tiro, poço gravitacional,
 * nascimento apertado) ficaria queimando enquanto continuasse perseguindo o
 * alvo através da mancha.
 *
 * Escreve em `out` em vez de devolver um ponto novo: roda dentro do laço de
 * quadro de cada bot que está queimando.
 */
export function findLavaEscape(x: number, z: number, out: { x: number; z: number }): boolean {
  for (let radius = 4; radius <= 12; radius += 4) {
    for (let i = 0; i < 12; i++) {
      const angle = (i / 12) * Math.PI * 2;
      const cx = x + Math.cos(angle) * radius;
      const cz = z + Math.sin(angle) * radius;
      if (!isLavaAt(cx, cz)) {
        out.x = cx;
        out.z = cz;
        return true;
      }
    }
  }
  return false;
}

/**
 * A lava desvia, não bloqueia.
 *
 * Recebe o passo já calculado e o ponto de onde o tanque saiu; se o passo entra
 * na lava vindo de fora, tenta manter só o eixo que continua seguro. Quando não
 * há eixo seguro o passo passa inteiro: atravessa e paga o preço, como qualquer
 * um. Tratar a lava como parede faria o bot parar na borda como se houvesse um
 * muro invisível — e daria para prendê-lo ali de propósito.
 */
export function deflectFromLava(
  step: { x: number; z: number },
  fromX: number,
  fromZ: number,
): void {
  if (!arenaSurface.active) return;
  if (sampleSurface(step.x, step.z) !== "lava") return;
  // Já estava dentro: sair é o objetivo, não há o que desviar.
  if (sampleSurface(fromX, fromZ) === "lava") return;
  if (sampleSurface(step.x, fromZ) !== "lava") {
    step.z = fromZ;
  } else if (sampleSurface(fromX, step.z) !== "lava") {
    step.x = fromX;
  }
}

// ============================================
// MOVIMENTO SOBRE O PISO
// ============================================

/**
 * Ângulo mínimo entre para onde o tanque aponta e para onde ele realmente anda
 * para a derrapagem virar marca no chão. ~25°: abaixo disso é curva normal,
 * acima é o tanque de lado — que é o que merece deixar risco.
 */
const SKID_DOT_THRESHOLD = 0.9;
/** Abaixo desta velocidade (u/s) não há derrapagem que valha marca. */
const MIN_SKID_SPEED_SQ = 5 * 5;
/** Distância entre marcas do rastro, ao quadrado. */
export const TRAIL_STEP_SQ = 1.4 * 1.4;

/**
 * O tanque está de lado? Compara a direção real do movimento com a frente do
 * casco.
 */
export function isSkidding(velocity: { x: number; z: number }, rotationY: number): boolean {
  const speedSq = velocity.x * velocity.x + velocity.z * velocity.z;
  if (speedSq < MIN_SKID_SPEED_SQ) return false;
  const inv = 1 / Math.sqrt(speedSq);
  const dot = velocity.x * inv * Math.sin(rotationY) + velocity.z * inv * Math.cos(rotationY);
  return dot < SKID_DOT_THRESHOLD;
}

/**
 * Derrapagem: com tração cheia a velocidade obedece na hora; no gelo ela leva
 * um tempo para alcançar a direção pedida, e é essa defasagem que se lê como
 * "escorreguei". Muta o vetor recebido — roda a cada frame, e alocar um objeto
 * novo aqui seria lixo garantido no coletor.
 *
 * `velocity` está em unidades por segundo, não em deslocamento por frame: é o
 * que permite guardar o impulso entre frames de duração diferente e deixar o
 * gelo escorregar igual em qualquer FPS.
 */
export function applySurfaceVelocity(
  velocity: { x: number; z: number },
  targetX: number,
  targetZ: number,
  traction: number,
  dt: number,
): void {
  if (traction >= 1) {
    velocity.x = targetX;
    velocity.z = targetZ;
    return;
  }
  const k = frameRateAdjusted(traction, dt);
  velocity.x += (targetX - velocity.x) * k;
  velocity.z += (targetZ - velocity.z) * k;
}

/**
 * Sem comando o tanque não para: ele perde impulso no ritmo do piso.
 *
 * No chão firme isso dura um terço de segundo e passa por "freou". No gelo passa
 * de dois — e é aí que o tanque continua indo para onde já ia enquanto tenta
 * corrigir. O vetor inteiro decai junto com o escalar, senão a derrapagem
 * terminaria num corte seco em vez de morrer sozinha.
 */
export function applyGlide(
  velocity: { x: number; z: number },
  currentSpeed: { current: number },
  glideSeconds: number,
  dt: number,
): void {
  const decay = glideDecay(glideSeconds, dt);
  currentSpeed.current *= decay;
  velocity.x *= decay;
  velocity.z *= decay;
  if (currentSpeed.current < 0.5) {
    currentSpeed.current = 0;
    velocity.x = 0;
    velocity.z = 0;
  }
}

// ============================================
// RASTRO
// ============================================

export type TrailKind = "water" | "mud" | "ice" | "scorch";

/**
 * Que marca este tanque deixa no chão agora.
 *
 * O rastro conta de onde o tanque veio, então o material tem de ser o do piso:
 * quem sai do charco arrasta lama, quem sai do lago pinga água, quem está
 * derrapando risca o gelo e quem está em chamas chamusca. Ordem de prioridade:
 * o que está acontecendo agora (derrapagem) vence o que ficou grudado (lama,
 * água), e este vence o fogo residual.
 */
export function pickTrailKind(
  status: Readonly<SurfaceStatus>,
  surface: SurfaceType,
  skidding: boolean,
  now: number,
): TrailKind | null {
  if (skidding && surface === "ice") return "ice";
  if (now < status.muddyUntil) return "mud";
  if (now < status.soakedUntil) return "water";
  if (now < status.burningUntil) return "scorch";
  return null;
}

/**
 * Decide *quando* a próxima marca sai, guardando o último ponto marcado.
 *
 * Uma marca a cada trecho percorrido, não por frame — senão o chão vira uma
 * faixa contínua e o custo sobe junto com o FPS. Uma instância por tanque.
 */
export class TrailSpacer {
  private lastX = 0;
  private lastZ = 0;
  private primed = false;

  /** `spacing` multiplica o passo padrão: >1 espaça mais (usado por bots). */
  shouldEmit(x: number, z: number, skidding: boolean, spacing = 1): boolean {
    if (!this.primed) {
      this.primed = true;
      this.lastX = x;
      this.lastZ = z;
      return false;
    }
    const dx = x - this.lastX;
    const dz = z - this.lastZ;
    // A derrapagem risca mais junto: são dois riscos contínuos de esteira, não
    // pegadas espaçadas.
    const step = (skidding ? TRAIL_STEP_SQ * 0.35 : TRAIL_STEP_SQ) * spacing * spacing;
    if (dx * dx + dz * dz <= step) return false;
    this.lastX = x;
    this.lastZ = z;
    return true;
  }

  reset(x = 0, z = 0): void {
    this.lastX = x;
    this.lastZ = z;
    this.primed = false;
  }
}
